import { useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { Button } from '@/shared/components/ui';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import { Navbar } from '@/shared/components/Navbar';
import { Footer } from '@/shared/components/Footer';
import { LanguageSwitcher } from '@/shared/components/LanguageSwitcher';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { AuthLoadingFallback } from '@/features/auth/components/AuthLoadingFallback';
import { authPhoneUrl, AUTH_INTENT_GUIDE } from '@/features/auth/constants';
import { LinkPhoneDialog } from '@/features/auth/components/LinkPhoneDialog';
import { GuideApplicationForm } from '../components/GuideApplicationForm';
import { GuideApplicationSubmitted } from '../components/GuideApplicationSubmitted';

/**
 * The public "Become a Guide" entry point.
 *
 * This page no longer mutates anything on mount. The previous version fired
 * `POST /guide-profile/become` from a `useEffect`, so merely visiting the URL —
 * from a bookmark, the back button, or a crawler following the nav link —
 * created a guide profile. Applying is now an explicit form submission.
 *
 * The route deliberately sits outside `AppLayout` (the traveler
 * `RoleSurfaceGate` there redirected visitors before this could mount), so the
 * page renders the shared `Navbar`/`Footer` chrome itself.
 */

const BENEFITS = [
  { key: 'verified', icon: VerifiedUserOutlinedIcon },
  { key: 'identity', icon: BadgeOutlinedIcon },
  { key: 'rates', icon: PaidOutlinedIcon },
] as const;

const STORY_IMAGES = [
  { src: '/images/destinations/guide-travelers.jpg', altKey: 'guideWithTravellers' },
  { src: '/images/destinations/explore-local.jpg', altKey: 'exploringStreet' },
  { src: '/images/destinations/choose-guide.jpg', altKey: 'mountainView' },
] as const;

/**
 * Walkthrough of the application, for applicants who get stuck on the form.
 * The privacy-enhanced domain keeps YouTube from setting cookies until the
 * visitor actually presses play.
 */
const WALKTHROUGH_VIDEO_URL = 'https://www.youtube-nocookie.com/embed/UQIZt_dIrW0';

/** Height the fixed navbar occupies, which the hero has to clear from the top. */
const NAVBAR_HEIGHT = { xs: 64, md: 72 };

/**
 * Navbar + Footer wrapper, so every state of this page keeps the site chrome.
 * `overlayHero` is false on the success screen: there is no photograph behind
 * the bar there, so it needs its solid surface.
 */
function PageShell({ children, overlayHero }: { children: ReactNode; overlayHero: boolean }) {
  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Navbar overlayHero={overlayHero} />
      <Box component="main" sx={{ flex: 1, width: '100%' }}>
        {children}
      </Box>
      <Footer />
    </Box>
  );
}

export function BecomeGuidePage() {
  const navigate = useNavigate();
  const { t } = useTranslation('guideProfile');
  const { isInitializing, isAuthenticated, guideLifecycleRoute, user } = useAuth();
  const [submitted, setSubmitted] = useState<null | { alreadyApplied: boolean }>(null);
  const [linkPhoneOpen, setLinkPhoneOpen] = useState(false);

  if (isInitializing) {
    return <AuthLoadingFallback />;
  }

  // Anyone who already has an application goes straight to their own screen.
  if (isAuthenticated && guideLifecycleRoute) {
    return <Navigate to={guideLifecycleRoute} replace />;
  }

  if (submitted) {
    // `overlayHero` so the navbar floats over the photograph, same as the
    // application screen it just came from.
    return (
      <PageShell overlayHero>
        <GuideApplicationSubmitted
          alreadyApplied={submitted.alreadyApplied}
          onSignIn={() => navigate(authPhoneUrl(AUTH_INTENT_GUIDE))}
          onBackHome={() => navigate('/')}
        />
      </PageShell>
    );
  }

  return (
    <PageShell overlayHero>
      {/* Hero — the onboarding photograph runs full bleed behind both columns. */}
      <Box
        component="section"
        sx={{ position: 'relative', overflow: 'hidden', bgcolor: 'common.black' }}
      >
        {/*
          Rendered as an <img> rather than a CSS background so it is a real
          decoded image the browser can prioritise, matching `HeroBackground`
          on the home page.
        */}
        <Box
          component="img"
          src="/images/guideOnboarding.png"
          alt=""
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: { xs: '62% center', md: 'center center' },
            display: 'block',
          }}
        />

        {/*
          The same deep-navy wash the home hero uses: heavy on the left so the
          copy holds contrast over a bright sunset sky, thinning toward the
          right so the fort and the guide stay plainly visible. A white wash
          was tried first and left the photograph looking washed out.
        */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: {
              xs: 'linear-gradient(180deg, rgba(15,23,42,0.80) 0%, rgba(15,23,42,0.68) 45%, rgba(15,23,42,0.62) 100%)',
              md: `linear-gradient(
                100deg,
                rgba(15, 23, 42, 0.90) 0%,
                rgba(15, 32, 64, 0.78) 30%,
                rgba(20, 45, 90, 0.45) 50%,
                rgba(20, 45, 90, 0.16) 66%,
                rgba(15, 23, 42, 0.10) 100%
              )`,
            },
          }}
        />

        {/* Top vignette, so the transparent navbar stays legible. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 140,
            pointerEvents: 'none',
            background:
              'linear-gradient(to bottom, rgba(15, 23, 42, 0.55) 0%, transparent 100%)',
          }}
        />

        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            pt: {
              xs: `calc(${NAVBAR_HEIGHT.xs}px + 32px)`,
              md: `calc(${NAVBAR_HEIGHT.md}px + 40px)`,
            },
            pb: { xs: 5, md: 7 },
            px: { xs: 2, sm: 3, md: 6 },
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(380px, 440px)' },
              gap: { xs: 4, md: 5, lg: 6 },
              alignItems: 'start',
            }}
          >
            {/* Pitch */}
            <Box>
              {/* First thing on the page, so a guide who can't read English finds it before anything else. */}
              <LanguageSwitcher variant="glass" sx={{ mb: 2.5 }} />
              <Typography
                variant="overline"
                sx={{
                  display: 'block',
                  // The overline's default line-height adds space above the
                  // text, which sat it visibly lower than the form panel's top.
                  lineHeight: 1.5,
                  color: 'secondary.main',
                  letterSpacing: '0.14em',
                  fontWeight: 700,
                }}
              >
                {t('becomeGuide.eyebrow')}
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                  mt: 1,
                  mb: 2,
                  color: 'common.white',
                  fontSize: { xs: '2.1rem', sm: '2.6rem', md: '3rem' },
                  textShadow: '0 2px 18px rgba(15, 23, 42, 0.45)',
                }}
              >
                <Trans
                  t={t}
                  i18nKey="becomeGuide.title"
                  components={{
                    highlight: (
                      <Box component="span" sx={{ display: 'block', color: 'primary.300' }} />
                    ),
                  }}
                />
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  lineHeight: 1.75,
                  mb: 3.5,
                  maxWidth: 600,
                  color: 'rgba(255, 255, 255, 0.82)',
                }}
              >
                {t('becomeGuide.intro')}
              </Typography>

              {/* Walkthrough video — right under the intro so it's at hand while filling the form. */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ color: 'common.white', mb: 0.5 }}
                >
                  {t('becomeGuide.walkthrough.title')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ lineHeight: 1.6, color: 'rgba(255, 255, 255, 0.75)', mb: 1.5 }}
                >
                  {t('becomeGuide.walkthrough.body')}
                </Typography>
                <Box
                  sx={{
                    position: 'relative',
                    aspectRatio: '16 / 9',
                    overflow: 'hidden',
                    borderRadius: '10px',
                    border: '1px solid rgba(255, 255, 255, 0.22)',
                    boxShadow: '0 20px 48px rgba(15, 23, 42, 0.28)',
                    bgcolor: 'common.black',
                  }}
                >
                  <Box
                    component="iframe"
                    src={WALKTHROUGH_VIDEO_URL}
                    title={t('becomeGuide.walkthrough.videoTitle')}
                    loading="lazy"
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                  />
                </Box>
              </Box>

              {/* Benefits — glass cards matching the application panel. */}
              <Box
                sx={{
                  mt: 3,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                  gap: 1.5,
                }}
              >
                {BENEFITS.map(({ key, icon: Icon }) => (
                  <Box
                    key={key}
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: { xs: 'row', sm: 'column' },
                      gap: 1.5,
                      alignItems: 'flex-start',
                      bgcolor: 'rgba(15, 23, 42, 0.35)',
                      backdropFilter: 'blur(16px)',
                      WebkitBackdropFilter: 'blur(16px)',
                      border: '1px solid rgba(255, 255, 255, 0.18)',
                      borderRadius: '10px',
                    }}
                  >
                    <Box
                      sx={(theme) => ({
                        width: 38,
                        height: 38,
                        flexShrink: 0,
                        borderRadius: (t) => t.radii.md,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid',
                        borderColor: alpha(theme.palette.secondary.main, 0.45),
                        bgcolor: alpha(theme.palette.secondary.main, 0.2),
                        color: 'secondary.light',
                      })}
                    >
                      <Icon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ color: 'common.white', mb: 0.5, lineHeight: 1.3 }}
                      >
                        {t(`becomeGuide.benefits.${key}.title`)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ lineHeight: 1.55, fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.75)' }}
                      >
                        {t(`becomeGuide.benefits.${key}.body`)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            {/*
              Application — the same glass panel the home hero uses for its
              search card, so the two landing surfaces read as one system.
            */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3.5 },
                bgcolor: 'rgba(15, 23, 42, 0.35)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: '1px solid rgba(255, 255, 255, 0.22)',
                borderRadius: '10px',
                boxShadow: '0 20px 48px rgba(15, 23, 42, 0.28)',
                position: { md: 'sticky' },
                top: { md: 96 },
              }}
            >
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ mb: 1, color: 'common.white' }}
              >
                {t('becomeGuide.applicationCard.title')}
              </Typography>

              {/*
                Signed in, but this account has no application. Usually it means
                they signed in with a Google address that differs from the one they
                applied with. Without this notice they would simply see a blank
                form and start a second application on a second account.
              */}
              {isAuthenticated && (
                <Alert
                  severity="info"
                  variant="outlined"
                  sx={{
                    mt: 2,
                    mb: 1,
                    color: 'rgba(255, 255, 255, 0.86)',
                    bgcolor: 'rgba(255, 255, 255, 0.08)',
                    borderColor: 'rgba(255, 255, 255, 0.28)',
                    '& .MuiAlert-icon': { color: 'info.light' },
                  }}
                >
                  <AlertTitle sx={{ fontWeight: 700, color: 'common.white' }}>
                    {t('becomeGuide.noApplication.title')}
                  </AlertTitle>
                  {t('becomeGuide.noApplication.body', {
                    account: user?.email || t('becomeGuide.noApplication.thisAccount'),
                  })}
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setLinkPhoneOpen(true)}
                    >
                      {t('becomeGuide.noApplication.linkPhone')}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate(authPhoneUrl(AUTH_INTENT_GUIDE))}
                    >
                      {t('becomeGuide.noApplication.signInWithPhone')}
                    </Button>
                  </Box>
                </Alert>
              )}

              <GuideApplicationForm onApplied={setSubmitted} />

              <Divider sx={{ my: 2.5, borderColor: 'rgba(255, 255, 255, 0.18)' }} />

              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{ mb: 1.5, color: 'rgba(255, 255, 255, 0.72)' }}
                >
                  {t('becomeGuide.alreadyApplied.prompt')}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => navigate(authPhoneUrl(AUTH_INTENT_GUIDE))}
                  fullWidth
                  sx={{
                    py: 1.25,
                    fontWeight: 600,
                    color: 'common.white',
                    borderColor: 'rgba(255, 255, 255, 0.45)',
                    '&:hover': {
                      borderColor: 'common.white',
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  {t('becomeGuide.alreadyApplied.action')}
                </Button>
              </Box>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* Closing band — why this matters, in the guide's own terms. */}
      <Box
        component="section"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          bgcolor: '#FDF4E6',
          borderTop: '1px solid',
          borderColor: alpha('#D97706', 0.12),
          py: { xs: 5, md: 6 },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(120% 90% at 100% 50%, rgba(217,119,6,0.09) 0%, rgba(217,119,6,0) 60%)',
          }}
        />
        <Container
          maxWidth="lg"
          sx={{ position: 'relative', px: { xs: 2, sm: 3, md: 6 } }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'auto minmax(0, 1fr)' },
              gap: { xs: 3, md: 6 },
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {STORY_IMAGES.map(({ src, altKey }) => (
                <Box
                  key={src}
                  component="img"
                  src={src}
                  alt={t(`becomeGuide.storyImages.${altKey}`)}
                  loading="lazy"
                  sx={{
                    width: { xs: 96, sm: 120 },
                    height: { xs: 70, sm: 84 },
                    objectFit: 'cover',
                    borderRadius: (t) => t.radii.lg,
                    boxShadow: '0 6px 16px -6px rgba(15, 23, 42, 0.25)',
                  }}
                />
              ))}
            </Box>

            <Box>
              <FormatQuoteIcon
                sx={{ color: alpha('#D97706', 0.35), fontSize: 40, mb: -1 }}
              />
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.25rem', md: '1.5rem' } }}
              >
                {t('becomeGuide.closing.title')}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ lineHeight: 1.75, maxWidth: 620 }}
              >
                {t('becomeGuide.closing.body')}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/*
        Reloads rather than patching local state: linking a phone can attach
        an existing application to this account, which changes the lifecycle
        route. A reload lets the single routing authority decide where they
        now belong instead of this page guessing.
      */}
      <LinkPhoneDialog
        open={linkPhoneOpen}
        onClose={() => setLinkPhoneOpen(false)}
        onLinked={() => {
          setLinkPhoneOpen(false);
          window.location.reload();
        }}
        onSignInWithPhoneInstead={() => {
          setLinkPhoneOpen(false);
          navigate(authPhoneUrl(AUTH_INTENT_GUIDE));
        }}
      />
    </PageShell>
  );
}
